// Fetches the corpus-wide entity index (all ThreatActor / MalwareFamily
// names + aliases) from the backend exactly once per session and caches
// the response so we can hand it to the autolinker on every render.
//
// Data is independent of the chat turn — it only changes when Malpedia
// is re-synced server-side. The endpoint emits an ETag and the response
// is sticky across reloads via localStorage keyed by that version.

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { EntityLink } from '../../types';

export interface EntityIndex {
  actors: EntityLink[];
  families: EntityLink[];
  version: string;
}

const STORAGE_KEY = 'malpedia.entityIndex.v1';

interface CachedEntry {
  version: string;
  data: EntityIndex;
}

function readCache(): CachedEntry | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedEntry;
    if (!parsed?.version || !parsed?.data) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(entry: CachedEntry): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    /* quota errors are non-fatal — the index is recomputed cheaply next load */
  }
}

async function fetchEntityIndex(): Promise<EntityIndex> {
  const cached = readCache();
  const headers: Record<string, string> = {};
  if (cached) headers['If-None-Match'] = `"${cached.version}"`;

  const res = await fetch('/api/fastapi/entities/index', {
    credentials: 'same-origin',
    headers,
  });

  if (res.status === 304 && cached) {
    return cached.data;
  }
  if (!res.ok) {
    if (cached) return cached.data;
    throw new Error(`entities/index failed: ${res.status}`);
  }

  const data = (await res.json()) as EntityIndex;
  if (data?.version) {
    writeCache({ version: data.version, data });
  }
  return data;
}

export function useEntityIndex(): EntityIndex | null {
  const q = useQuery<EntityIndex>({
    queryKey: ['entities', 'index'],
    queryFn: fetchEntityIndex,
    staleTime: Infinity,
    gcTime: 24 * 60 * 60 * 1000, // 24h
    retry: 1,
    refetchOnWindowFocus: false,
    initialData: () => readCache()?.data,
  });

  // Surface failures in the console once, without bubbling — the linker
  // gracefully degrades to the retrieval-scoped entity list.
  useEffect(() => {
    if (q.isError) {
      // eslint-disable-next-line no-console
      console.warn('useEntityIndex: corpus index unavailable', q.error);
    }
  }, [q.isError, q.error]);

  return q.data ?? null;
}
